import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { createGlobalStyle } from 'styled-components';
import {
  kleinBlue,
  gold,
  fuchsia,
  black,
} from '@spotify-internal/encore-foundation';
import { DEFAULT_SOCIAL_CARD } from '../../../../features/Layout/PageHead';

const goldHighlight = '#fbb250';
const goldActive = '#d58010';
const kleinBlueActive = '#2E00AC';
const kleinBlueHighlight = '#7140F8';

const fuchsiaHighlight = 'rgb(229, 79, 169)';
const fuchsiaActive = 'rgb(154, 14, 98)';

const defaultTitle = process.env.REACT_APP_TITLE;
const defaultDescription = process.env.REACT_APP_DESCRIPTION;
const defaultImage = DEFAULT_SOCIAL_CARD;

// Apply gold theme
// eslint-disable-next-line no-unused-expressions
export const PageHeadStyles = createGlobalStyle`

  .nav-tabs li.active a::after,
  .no-touchevents .nav-tabs li:active a::after,
  .no-touchevents .nav-tabs li:hover a::after {
    background: ${black};
  }

  a:focus,
  a:hover {
    &,
    .footer-default .nav &,
    .navbar-default .navbar-nav .open .dropdown-menu>li> & {
      color: ${goldHighlight};
    }
  }

  a.external {
    color: ${gold};

    &::after {
      background-image: url('data:image/svg+xml;utf8,<svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><title>Arrow</title><g stroke="${goldHighlight}" stroke-width="2" fill="none" fill-rule="evenodd"><path d="M1 9l8-8"/><path stroke-linejoin="round" d="M2 .998h7V8"/></g></svg>')
    }

    &:hover {
      color: ${goldHighlight};
    }
  }

  .btn-green {
    background-color: ${kleinBlue};

    &:hover {
      background-color: ${kleinBlueHighlight};
    }

    &:active,
    &:focus {
      &,
      &:hover {
        background-color: ${kleinBlueActive};
      }
    }
  }

  .btn-fuchsia {
    background-color: ${fuchsia};

    &:hover {
      background-color: ${fuchsiaHighlight};
    }

    &:active,
    &:focus {
      &,
      &:hover {
        background-color: ${fuchsiaActive};
      }
    }
  }
`;

export default function PageHead(props) {
  const title = [...props.title, defaultTitle].join(' – ');
  const description = props.description || defaultDescription;
  const image =
    (props.image || defaultImage || '').replace(/^\/\//, 'https://') || null;
  const linkedData = props.linkedData && {
    '@context': 'http://schema.org',
    ...props.linkedData,
  };

  return (
    <Head>
      <title key="title">{title}</title>
      <meta key="meta.description" name="description" content={description} />
      <meta name="apple-itunes-app" content="app-id=1222021797" />
      <meta name="twitter:app:id:googleplay" content="com.spotify.music" />

      <meta key="meta.og:title" name="og:title" content={title} />
      <meta
        key="meta.og:description"
        name="og:description"
        content={description}
      />
      <meta
        key="meta.og:image"
        name="og:image"
        property="og:image"
        content={image}
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta key="meta.twitter:title" name="twitter:title" content={title} />
      <meta key="meta.twitter:image" name="twitter:image" content={image} />
      <meta
        key="meta.twitter:description"
        name="twitter:description"
        content={description}
      />
      <meta name="twitter:site" content="@spotifyartists" />
      <meta name="twitter:app:id:iphone" content="324684580" />
      <meta name="twitter:app:id:googleplay" content="com.spotify.music" />

      <link
        rel="alternate"
        type="application/rss+xml"
        title="All Posts – News – Spotify for Artists"
        href="/blog.rss"
        key="link.blogUrl"
      />

      {linkedData && (
        <script
          key="script.linkedData"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(linkedData) }}
        />
      )}
      {props.children}
    </Head>
  );
}

PageHead.propTypes = {
  title: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
  image: PropTypes.string,
  linkedData: PropTypes.shape({}),
  children: PropTypes.node,
};

PageHead.defaultProps = {
  title: [],
};
